import React from "react";
import Layout, { Main } from "../Layout";
import styled, { css } from "styled-components";
import { Link, Navigate } from "react-router-dom";
import { useApi } from "../Api/ApiContext";
import { useAsyncFn } from "react-use";
import { Col, Row, Spinner, Alert, Button } from "reactstrap";
import useCelebritiesStore from "../store/CelebritiesStore";
import imageCompression from "browser-image-compression";

const allowedTypes = ["image/jpeg", "image/png", "image/svg"];
const limitSize = 200;

const AddLabel = styled.label`
  display: inline-block;
  min-width: fit-content;
  font-size: 14px;
  padding: 10px;
  width: fit-content;
  border: ${({ theme }) => (theme.mode === "dark" ? "white" : "black")} 2px
    dashed;
  cursor: pointer;
`;

const ImageInput = styled.input`
  position: absolute;
  max-width: 200px;
  left: 0;
  opacity: 0;
  z-index: -1;
`;

const Image = styled.img`
  width: 150px;
  height: 150px;
  ${(props) =>
    props.$hasImage &&
    css`
      border-radius: 50%;
      border: solid 2px
        ${({ theme }) => (theme.mode === "dark" ? "white" : "black")};
    `}
`;

const Profile = ({ me }) => {
  const { saveAvatar } = useApi();
  const [{ value, loading, error }, saveImage] = useAsyncFn((imageToUpload) =>
    saveAvatar(imageToUpload)
  );
  const [imageToUpload, setImageToUpload] = React.useState(null);
  const [imageError, setImageError] = React.useState(false);
  const [profileImage] = React.useState(me.avatar);
  const [editPersonalInfo, setEditPersonalInfo] = React.useState(false);
  const [imageSaveSuccessfully, setImageSaveSuccessfully] =
    React.useState(false);
  const { celebrities } = useCelebritiesStore();

  const handleSaveImage = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("avatar", imageToUpload);
    saveImage(formData).then((res) => {
      setImageSaveSuccessfully(true);
      setImageToUpload(null);
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const compressedFile = await imageCompression(file, {
      maxSizeMB: 0.2, // ~200 Ko max
      maxWidthOrHeight: 512, // avatar max 512px
      useWebWorker: true,
    });

    if (allowedTypes.includes(compressedFile.type)) {
      setImageToUpload(compressedFile);
      setImageError(false);
    } else {
      setImageError(true);
      setImageToUpload(file);
    }
    setImageSaveSuccessfully(false);
  };

  const handleDelete = (e) => {
    setImageToUpload(null);
    setImageError(false);
  };

  if (editPersonalInfo) {
    return (
      <Navigate
        to={"/auth/sign-up"}
        state={{
          backPath: { pathname: "/admin/profile" },
        }}
      />
    );
  }

  if (loading) {
    return (
      <Layout backPath={{ pathname: "/admin" }} celebrities={celebrities.items}>
        <Main>
          <Row>
            <Col lg={2} className={"mx-auto"}>
              <Spinner color={"danger"} />
            </Col>
          </Row>
        </Main>
      </Layout>
    );
  }

  return (
    <Layout backPath={{ pathname: "/admin" }} celebrities={celebrities.items}>
      <Main className={"my-3"}>
        <Row>
          <Col xs={12} sm={8} lg={6} className={"mx-auto"}>
            <Row className={"align-items-center"}>
              <Col xs={12} sm={4} className={"mx-auto mx-sm-0  text-center"}>
                <Image
                  className={"object-fit-cover "}
                  $hasImage={
                    !!imageToUpload ||
                    value?.image !== undefined ||
                    profileImage
                  }
                  src={
                    imageToUpload
                      ? window.URL.createObjectURL(imageToUpload)
                      : value
                      ? value.image
                      : profileImage
                  }
                  title={`photo de ${me.nickname}`}
                  alt={`photo de ${me.nickname}`}
                />
              </Col>
              <Col xs={12} sm={8}>
                <Row>
                  <Col
                    xs={6}
                    sm={6}
                    lg={12}
                    className={"mx-auto mx-sm-0 mt-2 mt-sm-0"}
                  >
                    <AddLabel htmlFor="add">
                      {profileImage ? "Modifer la" : "Ajouter une"} photo
                    </AddLabel>
                    <ImageInput
                      id="add"
                      type="file"
                      value={""}
                      onChange={(e) => handleImageChange(e)}
                      accept=".jpg, .jpeg, .png, .svg"
                    />
                  </Col>
                  <Col xs={12} className={"text-center text-sm-start my-2"}>
                    {error && (
                      <Alert color={"danger"}>Une erreur est survenue.</Alert>
                    )}
                    {imageError ? (
                      <Alert color={"danger"}>
                        image non valide. <br /> Taille et/ou format non
                        supporté.
                      </Alert>
                    ) : (
                      <span>
                        Format accepté : jpeg, png, svg.
                        <br /> Taille maximum : {limitSize} Ko
                      </span>
                    )}
                  </Col>
                </Row>
                <Row
                  className={"justify-content-around justify-content-sm-start"}
                >
                  <Col xs={4} sm={4}>
                    <Button
                      size={"sm"}
                      color={"primary"}
                      onClick={(e) => handleSaveImage(e)}
                      disabled={!imageToUpload || imageSaveSuccessfully}
                    >
                      Enregistrer
                    </Button>
                  </Col>
                  <Col xs={4} sm={4}>
                    <Button
                      size={"sm"}
                      color={"secondary"}
                      onClick={(e) => handleDelete(e)}
                      disabled={!imageToUpload || imageSaveSuccessfully}
                    >
                      Annuler
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className={"mt-3"}>
          <Col sm={8} lg={6} className="mx-auto">
            <h1 className={"fs-4"}>Informations personnelles</h1>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              Email : <span className={"fw-bold"}>{me && me.email}</span>
            </p>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              Nom et prénoms :{" "}
              <span className={"fw-bold"}>
                {me && me.familyName + " " + me.givenName}
              </span>
            </p>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              Pseudo : <span className={"fw-bold"}>{me && me.nickname}</span>
            </p>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              Numero de téléphone - paiement mobile :{" "}
              <span className={"fw-bold"}>{me && me.phoneNumber}</span>
            </p>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              Numero de contact (whatsapp) :{" "}
              <span className={"fw-bold"}>{me && me.whatsappNumber}</span>
            </p>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              Pays de résidence :{" "}
              <span className={"fw-bold"}>{me && me.locale}</span>
            </p>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              Profession : <span className={"fw-bold"}>{me && me.title}</span>
            </p>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              Catégorie :{" "}
              <span className={"fw-bold"}>{me && me.category.name}</span>
            </p>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              Devise des transactions (monnaie) :{" "}
              <span className={"fw-bold"}>{me && me.currency}</span>
            </p>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              Délai de livraison des commandes :{" "}
              <span className={"fw-bold"}>{me && me.delay} heures</span>
            </p>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              Êtes-vous disponible ? :{" "}
              <span className={"fw-bold"}>
                {me && me.available === "yes" ? "Oui" : "Non"}
              </span>
            </p>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              Supportez-vous une association ? :{" "}
              <span className={"fw-bold"}>
                {me && me.supportAssociation ? "Oui" : "Non"}
              </span>
            </p>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className="mx-auto">
            <p>Description :</p>
            <div className={"fw-bold"}>{me && me.description}</div>
          </Col>
        </Row>
        <Row className={"mt-2"}>
          <Col sm={8} lg={6} className={"mx-auto "}>
            <Row>
              <Col className="mx-auto">
                <p>Vos réseaux sociaux :</p>
              </Col>
            </Row>
            <Row>
              {me && me.facebook && (
                <Col xs={2} lg={2} className={"text-center"}>
                  <Link to={me.facebook} title={"facebook"}>
                    <div>
                      <i className="fa-brands fa-facebook fs-4" />
                    </div>
                  </Link>
                </Col>
              )}
              {me && me.tiktok && (
                <Col xs={2} lg={2} className={"text-center"}>
                  <Link to={me.tiktok} title={"tiktok"}>
                    <div>
                      <i className="fa-brands fa-tiktok fs-4" />
                    </div>
                  </Link>
                </Col>
              )}
              {me && me.instagram && (
                <Col xs={2} lg={2} className={"text-center"}>
                  <Link to={me.instagram} title={"instagram"}>
                    <div>
                      <i className="fa-brands fa-square-instagram fs-4" />
                    </div>
                  </Link>
                </Col>
              )}
              {me && me.twitter && (
                <Col xs={2} lg={2} className={"text-center"}>
                  <Link to={me.twitter} title={"X (ex twitter"}>
                    <div>
                      <i className="fa-brands fa-square-x-twitter fs-4" />
                    </div>
                  </Link>
                </Col>
              )}
              {me && me.linkedin && (
                <Col xs={2} lg={2} className={"text-center"}>
                  <Link to={me.linkedin} title={"linkedin"}>
                    <div>
                      <i className="fa-brands fa-linkedin fs-4" />
                    </div>
                  </Link>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
        <Row className={"mt-3"}>
          <Col>
            <Button
              className={"col-12 col-sm-2 col-lg-1 offset-sm-8 offset-0 "}
              color="primary"
              onClick={(e) => setEditPersonalInfo(true)}
            >
              Modifier
            </Button>
          </Col>
        </Row>
      </Main>
    </Layout>
  );
};

export default Profile;
